var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useState, useEffect } from 'react';
import { noop } from 'lodash-es';
import styled from '../../../../styled';
import * as Icons from '../../../icons';
import * as Tooltip from '../../../tooltip';
import TooltipView from '../../../tooltip/TooltipView';
import { StyledInput } from '../../../input';
import { InputFocusBg, InputFocusBgWrapper as RawInputFocusBgWrapper, } from '../../styled';
import { ANCHOR_FROM, ANCHOR_TO, ANCHOR_NONE } from '../constants';
import { ClearBtnWrapper, DateInputIconWrapper, Wrapper, InputWrapper as InputWrapperComponent, } from './DateInput';
const ArrowIconWrapper = styled.div `
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
  margin-left: 0.0625rem;
  margin-right: 0.0625rem;
`;
const InputWrapper = styled(InputWrapperComponent) `
  padding-right: 0;

  .tooltip-container {
    display: inline-block;
    flex: unset;
    vertical-align: bottom;

    > div:last-of-type {
      height: 2.25rem;
      display: flex;
    }
  }
`;
const InputFocusBgWrapper = styled(RawInputFocusBgWrapper) `
  :nth-child(n + 1) {
    padding-right: 0.5rem;
  }
`;
const DateRangeInput = (_a) => {
    var { lang, status = 'valid', block = false, disabled, fromRef, toRef, inputSize = 'MEDIUM', selectedFrom, selectedTo, valueFrom, valueTo, placeholders = [], tooltipText, tooltipDirection = 'TOP_CENTER', clearBtnTooltipText, onClearBtnClick, forceDisabledValueFrom, forceDisableCalendarView, focusColor } = _a, restProps = __rest(_a, ["lang", "status", "block", "disabled", "fromRef", "toRef", "inputSize", "selectedFrom", "selectedTo", "valueFrom", "valueTo", "placeholders", "tooltipText", "tooltipDirection", "clearBtnTooltipText", "onClearBtnClick", "forceDisabledValueFrom", "forceDisableCalendarView", "focusColor"]);
    const [focusAnchor, setFocusAnchor] = useState(ANCHOR_NONE);
    const [isShowTooltip, setIsShowTooltip] = useState(false);
    const [isShowClearBtn, setIsShowClearBtn] = useState(false);
    const shouldDisableValueFrom = forceDisabledValueFrom && !!valueFrom;
    const handleBlur = () => {
        setFocusAnchor(ANCHOR_NONE);
        setIsShowTooltip(false);
    };
    const handleWrapperMouseEnter = useCallback(() => {
        if (selectedFrom && selectedTo)
            setIsShowClearBtn(true);
    }, [selectedFrom, selectedTo]);
    const handleWrapperMouseLeave = useCallback(() => {
        setIsShowClearBtn(false);
    }, []);
    const handleClearBtnClick = useCallback((e) => {
        e.stopPropagation();
        if (!fromRef || !fromRef.current)
            return;
        fromRef.current.focus();
        setIsShowClearBtn(false);
        setIsShowTooltip(true);
        if (onClearBtnClick)
            onClearBtnClick(e);
    }, [fromRef, onClearBtnClick]);
    useEffect(() => {
        if (!(fromRef === null || fromRef === void 0 ? void 0 : fromRef.current) || !(toRef === null || toRef === void 0 ? void 0 : toRef.current))
            return noop;
        const fromInput = fromRef.current;
        const toInput = toRef.current;
        const handleFocus = (anchor) => {
            setFocusAnchor(anchor);
            setIsShowTooltip(fromInput.value.length === 0 && toInput.value.length === 0);
        };
        const handleFromFocus = () => handleFocus(ANCHOR_FROM);
        const handleToFocus = () => handleFocus(ANCHOR_TO);
        const handleKeyup = (e) => {
            if (e.target === toInput && e.key === 'Enter') {
                setIsShowTooltip(false);
                return;
            }
            setIsShowTooltip(!!focusAnchor &&
                fromInput.value.length === 0 &&
                toInput.value.length === 0);
        };
        // init
        fromInput.addEventListener('focus', handleFromFocus);
        toInput.addEventListener('focus', handleToFocus);
        fromInput.addEventListener('keyup', handleKeyup);
        toInput.addEventListener('keyup', handleKeyup);
        return () => {
            fromInput.removeEventListener('focus', handleFromFocus);
            toInput.removeEventListener('focus', handleToFocus);
            fromInput.addEventListener('keyup', handleKeyup);
            toInput.addEventListener('keyup', handleKeyup);
        };
    }, [fromRef, focusAnchor, toRef]);
    useEffect(() => {
        // hide tooltip after selecting a new valid date from calendar view
        if (selectedFrom || selectedTo)
            setIsShowTooltip(false);
    }, [selectedFrom, selectedTo]);
    return (React.createElement(Wrapper, { disabled: disabled, focused: !!focusAnchor, onMouseEnter: handleWrapperMouseEnter, onMouseLeave: handleWrapperMouseLeave, onBlur: handleBlur, status: status, block: block },
        React.createElement(InputWrapper, { disabled: disabled },
            React.createElement(InputFocusBgWrapper, null,
                focusAnchor === ANCHOR_FROM && (React.createElement(InputFocusBg, { focusColor: focusColor, inputSize: inputSize }, valueFrom)),
                React.createElement(StyledInput, Object.assign({ ref: fromRef, type: "input", status: "valid", disabled: disabled || shouldDisableValueFrom, inputSize: inputSize, value: valueFrom, placeholder: placeholders[0], block: block }, restProps))),
            React.createElement(TooltipView, { type: "primary", lang: lang, text: tooltipText, show: isShowTooltip, direction: tooltipDirection, containerClassName: "tooltip-container" },
                React.createElement(ArrowIconWrapper, null,
                    React.createElement(Icons.Outline.Arrow, { position: "RIGHT", color: "INK_600", size: "MEDIUM" }))),
            React.createElement(InputFocusBgWrapper, { onClick: () => { var _a; return (_a = toRef === null || toRef === void 0 ? void 0 : toRef.current) === null || _a === void 0 ? void 0 : _a.focus(); } },
                focusAnchor === ANCHOR_TO && (React.createElement(InputFocusBg, { focusColor: focusColor, inputSize: inputSize }, valueTo)),
                React.createElement(StyledInput, Object.assign({ ref: toRef, type: "input", status: "valid", disabled: disabled, inputSize: inputSize, value: valueTo, placeholder: placeholders[1], block: block }, restProps)))),
        React.createElement(Tooltip.Primary, { lang: lang, text: clearBtnTooltipText, direction: tooltipDirection },
            React.createElement(ClearBtnWrapper, { show: isShowClearBtn && !shouldDisableValueFrom, inputSize: inputSize, onClick: handleClearBtnClick },
                React.createElement(Icons.Outline.Cross, { color: "INK_600", size: "SMALL" }))),
        !forceDisableCalendarView && (React.createElement(DateInputIconWrapper, { show: !isShowClearBtn && !shouldDisableValueFrom, disabled: disabled, inputSize: inputSize, onClick: handleClearBtnClick },
            React.createElement(Icons.Outline.Calendar, { color: "INK_600", size: "MEDIUM" })))));
};
export default DateRangeInput;
//# sourceMappingURL=DateRangeInput.js.map